import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import imageOptimizationService from '../services/imageOptimizationService';

import {
  getPubStepClientConfig,
  updatePubStepClientConfig,
  type PubStepClientConfig,
  type PubStepClientItem,
} from '../services/pubStepClientConfigService';

type Props = {
  storeId?: string | null;
  storeName?: string | null;
};

function normalizePositiveInt(v: string, fallback: number): number {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function normalizeNonNegative(v: string, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

const PubStepClientSettings: React.FC<Props> = ({
  storeId,
  storeName,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const [enabled, setEnabled] = React.useState(false);
  const [heightDp, setHeightDp] = React.useState<string>('32');
  const [intervalMs, setIntervalMs] = React.useState<string>('3500');
  const [slideDurationMs, setSlideDurationMs] = React.useState<string>('1200');
  const [items, setItems] = React.useState<PubStepClientItem[]>([]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const cfg: PubStepClientConfig = await getPubStepClientConfig(storeId);
      setEnabled(cfg.enabled === true);
      setHeightDp(String(typeof cfg.heightDp === 'number' ? cfg.heightDp : 32));
      setIntervalMs(String(typeof cfg.intervalMs === 'number' ? cfg.intervalMs : 3500));
      setSlideDurationMs(String(typeof cfg.slideDurationMs === 'number' ? cfg.slideDurationMs : 1200));
      setItems((cfg.items ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    } catch (e: any) {
      setError(e?.message || 'Erreur lors du chargement de la configuration Pub Step Client.');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const moveItem = (idx: number, dir: -1 | 1) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    const tmp = next[idx];
    next[idx] = next[j];
    next[j] = tmp;
    // re-order
    const reordered = next.map((it, i) => ({ ...it, order: i }));
    setItems(reordered);
  };

  const removeItem = (idx: number) => {
    const next = items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, order: i }));
    setItems(next);
  };

  const onAddImage = async (file: File) => {
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      // Use a stable id (used also for ordering)
      const id = `banner_${Date.now()}`;

      // Upload & optimize to pub_step_client/
      const urls = await imageOptimizationService.optimizeImageToBasePath(
        'pub_step_client',
        file,
        id,
        { format: 'webp', quality: 0.82 },
      );

      const newItem: PubStepClientItem = {
        id,
        order: items.length,
        imageUrl: urls.originalUrl,
        thumbnailUrl: urls.thumbnailUrl,
        mediumImageUrl: urls.mediumImageUrl,
        highImageUrl: urls.highImageUrl,
      };

      setItems((prev) => [...prev, newItem]);
      setSuccess('Image ajoutée. N’oublie pas de cliquer sur Enregistrer.');
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de l’upload.');
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: Partial<PubStepClientConfig> = {
        enabled,
        heightDp: normalizeNonNegative(heightDp, 32),
        intervalMs: normalizePositiveInt(intervalMs, 3500),
        slideDurationMs: normalizePositiveInt(slideDurationMs, 1200),
        items: items.map((it, i) => ({ ...it, order: i })),
      };

      await updatePubStepClientConfig(payload, { storeId });
      setSuccess('Configuration Pub Step Client enregistrée pour ce magasin.');
      setTimeout(() => void load(), 400);
    } catch (e: any) {
      setError(e?.message || 'Échec de l’enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Pub Step Client
      </Typography>

      {loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Chargement…</Typography>
        </Stack>
      ) : (
        <Box component="form" noValidate autoComplete="off">
          <Stack spacing={2} sx={{ mb: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
            {storeId && (
              <Alert severity="info">
                {`Cette configuration s’applique uniquement à ${storeName || storeId}.`}
              </Alert>
            )}
          </Stack>

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Activation</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <Typography>{enabled ? 'Bande pub activée' : 'Bande pub désactivée'}</Typography>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Hauteur (dp)"
              type="number"
              value={heightDp}
              onChange={(e) => setHeightDp(e.target.value)}
              helperText="Ex: 24, 32, 40"
              fullWidth
            />
            <TextField
              label="Interval (ms)"
              type="number"
              value={intervalMs}
              onChange={(e) => setIntervalMs(e.target.value)}
              helperText="Utile seulement si plusieurs images tournent dans la bande"
              fullWidth
            />
            <TextField
              label="Durée slide (ms)"
              type="number"
              value={slideDurationMs}
              onChange={(e) => setSlideDurationMs(e.target.value)}
              helperText="Valeur conseillée: 1200 ms"
              fullWidth
            />
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Images</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload vers Firebase Storage: <code>pub_step_client/</code>
            </Typography>

            <Button
              variant="outlined"
              component="label"
              disabled={saving}
            >
              Ajouter une image
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onAddImage(f);
                  e.currentTarget.value = '';
                }}
              />
            </Button>
          </Stack>

          <Stack spacing={1} sx={{ mb: 2 }}>
            {items.length === 0 ? (
              <Alert severity="info">Aucune image configurée.</Alert>
            ) : (
              items.map((it, idx) => {
                const previewHeight = normalizeNonNegative(heightDp, 32);
                // Simuler la largeur d’un téléphone (Flutter logical pixels ~ dp)
                // pour que l’admin règle la hauteur avec un rendu proche du client.
                const simulatedPhoneWidth = 390; // iPhone 12/13/14-ish

                return (
                  <Box
                    key={it.id}
                    sx={{
                      p: 1,
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography fontWeight="medium">{it.id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            order: {it.order}
                          </Typography>
                        </Box>

                        {/* Preview EXACT comme dans l'app client (BoxFit.contain) + largeur téléphone simulée */}
                        {it.imageUrl ? (
                          <Box
                            sx={{
                              width: simulatedPhoneWidth,
                              maxWidth: '100%',
                              height: previewHeight,
                              // Même fond que Step Client (turquoise StepBurger)
                              bgcolor: '#24B2B3',
                              overflow: 'hidden',
                              borderRadius: 1,
                              // Border visible pour aider à régler la hauteur
                              border: '2px solid rgba(36,178,179,0.9)',
                              boxShadow: '0 0 0 2px rgba(255,255,255,0.6) inset',
                              mx: 'auto',
                            }}
                            title="Aperçu comme dans l'app (contain + largeur téléphone simulée)"
                          >
                            <img
                              src={it.imageUrl}
                              alt={it.id}
                              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                            />
                          </Box>
                        ) : null}

                        {/* Preview image complète (respect ratio) */}
                        {it.imageUrl ? (
                          <Box
                            sx={{
                              width: simulatedPhoneWidth,
                              maxWidth: '100%',
                              maxHeight: 260,
                              overflow: 'auto',
                              borderRadius: 1,
                              border: '1px dashed rgba(0,0,0,0.25)',
                              bgcolor: '#fafafa',
                              mx: 'auto',
                            }}
                            title="Aperçu complet (contain)"
                          >
                            <img
                              src={it.imageUrl}
                              alt={it.id}
                              style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
                            />
                          </Box>
                        ) : null}
                      </Stack>
                    </Box>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <IconButton size="small" onClick={() => moveItem(idx, -1)} title="Monter">
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveItem(idx, 1)} title="Descendre">
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => removeItem(idx)} title="Supprimer">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })
            )}
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={onSave} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button variant="outlined" onClick={() => void load()} disabled={saving}>
              Recharger
            </Button>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              Optimisation activée: l’admin upload 4 fichiers dans Storage (<code>_thumb</code>, <code>_medium</code>, <code>_high</code>, <code>_orig</code>)
              dans <code>pub_step_client/</code>.
            </Alert>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PubStepClientSettings;
